import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { fileUrl } from "@/lib/placeholders";
import {
  AlertTriangle, Download, Edit, FileCheck2, FileText, Plus, Search,
  Trash2, Upload, X,
} from "lucide-react";

/**
 * I preventivi: si scrivono qui e si stampa il PDF, invece che in Word.
 *
 * Ricalcato sul modello aziendale "Offerta Base.docx". Il valore però non è il
 * PDF — quello lo faceva anche Word. È che un preventivo scritto qui ha uno
 * stato, e lo stato si vede: quanti ne ho mandati, quanti hanno detto di sì, e
 * soprattutto quali sono fermi da tre settimane senza risposta. In una cartella
 * di file .docx quella domanda non ha risposta.
 */
const COLORI = {
  bozza: "bg-slate-100 text-slate-600 border-slate-200",
  inviato: "bg-blue-50 text-blue-700 border-blue-200",
  accettato: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rifiutato: "bg-red-50 text-red-700 border-red-200",
  annullato: "bg-slate-100 text-slate-400 border-slate-200",
};

const euro = (n) =>
  `${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2,
                                              maximumFractionDigits: 2 })} €`;
const itDate = (iso) => (iso ? iso.substring(0, 10).split("-").reverse().join("/") : "—");
const vuota = () => ({ voce: "", descrizione: "", um: "a.c.", quantita: 1, prezzo_unitario: 0 });

export default function Preventivi() {
  const [dati, setDati] = useState(null);
  const [rif, setRif] = useState(null);
  const [carico, setCarico] = useState(true);
  const [cerca, setCerca] = useState("");
  const [stati, setStati] = useState([]);
  const [form, setForm] = useState(null);
  // Un selettore di file solo per tutta la tabella: si ricorda su quale
  // riga si sta caricando invece di ripetere un campo nascosto per riga.
  const [caricaSu, setCaricaSu] = useState(null);
  const selettore = useRef(null);

  const carica = useCallback(async () => {
    try {
      const [e, r] = await Promise.all([
        api.get("/preventivi"), api.get("/preventivi/riferimenti"),
      ]);
      setDati(e.data);
      setRif(r.data);
    } catch {
      toast.error("Errore nel caricamento dei preventivi");
    } finally { setCarico(false); }
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const righe = useMemo(() => {
    if (!dati) return [];
    const q = cerca.trim().toLowerCase();
    return dati.preventivi.filter((p) =>
      (stati.length === 0 || stati.includes(p.stato)) &&
      (!q || `${p.numero} ${p.cliente} ${p.oggetto}`.toLowerCase().includes(q)));
  }, [dati, cerca, stati]);

  const scarica = async (p) => {
    try {
      const { data, headers } = await api.get(`/preventivi/${p.id}/pdf`,
                                              { responseType: "blob" });
      const cd = headers["content-disposition"] || "";
      const nome = /filename="?([^";]+)"?/.exec(cd)?.[1] || `Preventivo ${p.numero}.pdf`;
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = nome;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore nella stampa"); }
  };

  const cambiaStato = async (p, stato) => {
    try {
      await api.post(`/preventivi/${p.id}/stato`, { stato });
      toast.success(`${p.numero} · ${stato}`);
      carica();
    } catch { toast.error("Errore"); }
  };

  const chiediIlFile = (p) => {
    setCaricaSu(p);
    selettore.current.value = "";
    selettore.current.click();
  };

  const caricaFirmato = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !caricaSu) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/preventivi/${caricaSu.id}/accettazione`, fd,
                     { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${caricaSu.numero} accettato · firmato allegato`);
      carica();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento");
    } finally { setCaricaSu(null); }
  };

  const elimina = async (p) => {
    if (!window.confirm(`Eliminare la bozza ${p.numero}?\n\nNon si annulla.`)) return;
    try {
      await api.delete(`/preventivi/${p.id}`);
      toast.success("Bozza eliminata");
      carica();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  if (carico) return <p className="text-sm text-slate-500">Caricamento...</p>;
  if (!dati || !rif) return null;

  const nomiStato = Object.fromEntries(dati.stati.map((s) => [s.codice, s.nome]));

  return (
    <div className="space-y-5" data-testid="preventivi-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 mb-2">Admin</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Preventivi</h1>
          <p className="text-sm text-slate-500 mt-1">
            Si compilano qui e si stampano · lo stato dice a che punto sono
          </p>
        </div>
        <Button onClick={() => setForm({ voci: [vuota()] })} data-testid="nuovo-preventivo"
                className="bg-orange-500 hover:bg-orange-600 text-white h-12">
          <Plus className="w-4 h-4 mr-2" /> Nuovo preventivo
        </Button>
      </div>

      {dati.da_richiamare > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 text-sm text-amber-900 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              <strong>{dati.da_richiamare}</strong>{" "}
              {dati.da_richiamare === 1 ? "preventivo è fermo" : "preventivi sono fermi"}
              {" "}da più di {rif.giorni_senza_risposta} giorni senza risposta.
              Sono quelli da richiamare.
            </span>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {dati.stati.map((s) => {
          const n = dati.conteggi[s.codice] || 0;
          const attivo = stati.includes(s.codice);
          return (
            <button key={s.codice} type="button" data-testid={`stato-${s.codice}`}
                    onClick={() => setStati((v) => v.includes(s.codice)
                      ? v.filter((x) => x !== s.codice) : [...v, s.codice])}
                    className={`rounded-lg border p-3 text-left transition-colors ${
                      COLORI[s.codice]} ${attivo ? "ring-2 ring-offset-1 ring-orange-400" : ""}`}>
              <div className="text-[11px] uppercase tracking-wide">{s.nome}</div>
              <div className="font-display text-2xl font-black">{n}</div>
              <div className="text-[11px]">{euro(dati.valori[s.codice])}</div>
            </button>
          );
        })}
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={cerca} onChange={(e) => setCerca(e.target.value)}
                   placeholder="Cerca per numero, cliente o oggetto" className="pl-9" />
          </div>

          {righe.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">
              Nessun preventivo con questi filtri.
            </p>
          ) : (
            <div className="overflow-auto border border-slate-200 rounded">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-600">
                  <tr>
                    <th className="text-left p-2">Numero</th>
                    <th className="text-left p-2">Data</th>
                    <th className="text-left p-2">Cliente</th>
                    <th className="text-left p-2">Oggetto</th>
                    <th className="text-right p-2">Totale</th>
                    <th className="text-left p-2">Stato</th>
                    <th className="text-right p-2">&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  {righe.map((p) => (
                    <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="p-2 font-mono whitespace-nowrap">{p.numero}</td>
                      <td className="p-2 whitespace-nowrap">{itDate(p.data)}</td>
                      <td className="p-2 font-medium">{p.cliente}</td>
                      <td className="p-2 text-slate-600 max-w-[280px] truncate" title={p.oggetto}>
                        {p.oggetto}
                      </td>
                      <td className="p-2 text-right tabular-nums whitespace-nowrap">
                        {euro(p.totale)}
                      </td>
                      <td className="p-2">
                        <select value={p.stato} data-testid={`cambia-stato-${p.id}`}
                                onChange={(e) => cambiaStato(p, e.target.value)}
                                className={`text-[11px] font-medium rounded-full border px-2 py-1 ${COLORI[p.stato]}`}>
                          {dati.stati.map((s) => (
                            <option key={s.codice} value={s.codice}>{s.nome}</option>
                          ))}
                        </select>
                        {p.da_richiamare && (
                          <div className="text-[10px] text-amber-700 mt-0.5">
                            fermo da {p.giorni_dall_invio} gg
                          </div>
                        )}
                      </td>
                      <td className="p-2">
                        <div className="flex items-center gap-1 justify-end">
                          <Button variant="outline" size="sm" title="Scarica il PDF"
                                  data-testid={`pdf-${p.id}`} onClick={() => scarica(p)}>
                            <Download className="w-3 h-3" />
                          </Button>
                          <Button variant="outline" size="sm" title="Modifica"
                                  onClick={() => setForm(p)}>
                            <Edit className="w-3 h-3" />
                          </Button>
                          {p.accettazione ? (
                            <a href={fileUrl(p.accettazione.file_url)}
                               target="_blank" rel="noopener noreferrer"
                               title={`Firmato · ${p.accettazione.filename || ""}`}>
                              <Button variant="outline" size="sm"
                                      data-testid={`firmato-${p.id}`}>
                                <FileCheck2 className="w-3 h-3 text-emerald-600" />
                              </Button>
                            </a>
                          ) : (
                            <Button variant="outline" size="sm"
                                    title="Carica il preventivo firmato"
                                    data-testid={`carica-firmato-${p.id}`}
                                    onClick={() => chiediIlFile(p)}>
                              <Upload className="w-3 h-3" />
                            </Button>
                          )}
                          {p.stato === "bozza" && (
                            <Button variant="outline" size="sm" title="Elimina la bozza"
                                    onClick={() => elimina(p)}>
                              <Trash2 className="w-3 h-3 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Il preventivo firmato che torna dal committente: caricarlo mette
          il preventivo in accettato, perche' e' quel foglio la prova. */}
      <input type="file" ref={selettore} className="hidden"
             accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff"
             data-testid="selettore-firmato" onChange={caricaFirmato} />

      {form && (
        <ModuloPreventivo iniziale={form} rif={rif}
                          onClose={() => setForm(null)}
                          onSaved={() => { setForm(null); carica(); }} />
      )}
    </div>
  );
}

/** Il modulo di redazione, ricalcato sul modello Word dell'azienda. */
function ModuloPreventivo({ iniziale, rif, onClose, onSaved }) {
  const nuovo = !iniziale.id;
  const [f, setF] = useState({
    data: (iniziale.data || new Date().toISOString()).substring(0, 10),
    luogo: iniziale.luogo || rif.luogo,
    cliente: iniziale.cliente || "",
    cliente_indirizzo: iniziale.cliente_indirizzo || "",
    oggetto: iniziale.oggetto || "",
    premessa: iniziale.premessa || rif.premessa,
    chiusura: iniziale.chiusura || rif.chiusura,
    avviso: iniziale.avviso || rif.avviso,
    reverse_charge: iniziale.reverse_charge || "",
    aliquota_iva: iniziale.aliquota_iva || "",
    validita_giorni: iniziale.validita_giorni || 30,
    note_interne: iniziale.note_interne || "",
  });
  const [voci, setVoci] = useState(
    (iniziale.voci && iniziale.voci.length ? iniziale.voci : [vuota()])
      .map((v) => ({ ...v })));
  const [busy, setBusy] = useState(false);

  const totale = voci.reduce(
    (s, v) => s + (Number(v.quantita) || 0) * (Number(v.prezzo_unitario) || 0), 0);

  const cambiaVoce = (i, campo, valore) =>
    setVoci((v) => v.map((x, k) => (k === i ? { ...x, [campo]: valore } : x)));

  const salva = async (e) => {
    e.preventDefault();
    const pulite = voci
      .filter((v) => (v.voce || v.descrizione || "").trim())
      .map((v) => ({ ...v, quantita: Number(v.quantita) || 0,
                     prezzo_unitario: Number(v.prezzo_unitario) || 0 }));
    setBusy(true);
    try {
      const corpo = { ...f, voci: pulite };
      if (nuovo) await api.post("/preventivi", corpo);
      else await api.put(`/preventivi/${iniziale.id}`, corpo);
      toast.success(nuovo ? "Preventivo creato" : "Preventivo aggiornato");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {nuovo ? "Nuovo preventivo" : `Preventivo ${iniziale.numero}`}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={salva} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label>Data</Label>
              <Input type="date" value={f.data} required
                     onChange={(e) => setF({ ...f, data: e.target.value })} /></div>
            <div><Label>Luogo</Label>
              <Input value={f.luogo}
                     onChange={(e) => setF({ ...f, luogo: e.target.value })} /></div>
            <div><Label>Validità (giorni)</Label>
              <Input type="number" min="0" value={f.validita_giorni}
                     onChange={(e) => setF({ ...f, validita_giorni: e.target.value })} /></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Spett.le *</Label>
              <Input value={f.cliente} required list="committenti-noti"
                     data-testid="preventivo-cliente"
                     placeholder="Ragione sociale o nome e cognome"
                     onChange={(e) => setF({ ...f, cliente: e.target.value })} />
              {/* I committenti già in anagrafica si suggeriscono, ma il campo
                  resta libero: molte offerte vanno a privati che in anagrafica
                  non ci sono e non ci devono stare. */}
              <datalist id="committenti-noti">
                {rif.committenti.map((c) => <option key={c.id} value={c.name} />)}
              </datalist>
            </div>
            <div><Label>Indirizzo del destinatario</Label>
              {/* Si scrive come su una busta: via, CAP e citta', l'ufficio a cui
                  va. Gli a capo battuti qui restano a capo anche sul PDF. */}
              <textarea value={f.cliente_indirizzo} rows={3}
                        data-testid="preventivo-indirizzo"
                        placeholder={"Via Sandro Pertini, 11\n20077 Melegnano (MI)"}
                        className="w-full border border-slate-200 rounded p-2 text-sm"
                        onChange={(e) => setF({ ...f, cliente_indirizzo: e.target.value })} /></div>
          </div>

          <div><Label>Oggetto *</Label>
            <Input value={f.oggetto} required data-testid="preventivo-oggetto"
                   placeholder="Preventivo per..."
                   onChange={(e) => setF({ ...f, oggetto: e.target.value })} /></div>

          <div><Label>Premessa</Label>
            <textarea value={f.premessa} rows={4}
                      className="w-full border border-slate-200 rounded p-2 text-sm"
                      onChange={(e) => setF({ ...f, premessa: e.target.value })} />
            <p className="text-xs text-slate-500 mt-1">
              È il testo standard dell&apos;azienda: si può cambiare su questo
              preventivo senza toccare gli altri.
            </p></div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Voci</Label>
              <Button type="button" variant="outline" size="sm"
                      data-testid="aggiungi-voce"
                      onClick={() => setVoci((v) => [...v, vuota()])}>
                <Plus className="w-3 h-3 mr-1" /> Aggiungi voce
              </Button>
            </div>
            {/* Ogni voce sta su due piani: sopra i campi corti, sotto la
                descrizione per tutta la larghezza. In una casella stretta una
                frase di due righe non si legge mentre la si scrive. */}
            <div className="space-y-3">
              {voci.map((v, i) => (
                <div key={i} className="border border-slate-200 rounded p-2 space-y-2">
                  <div className="grid grid-cols-12 gap-2 items-center">
                    <Input className="col-span-5" placeholder="Voce" value={v.voce}
                           data-testid={`voce-${i}`}
                           onChange={(e) => cambiaVoce(i, "voce", e.target.value)} />
                    <select className="col-span-2 h-10 border border-slate-200 rounded px-2 text-sm"
                            value={v.um} onChange={(e) => cambiaVoce(i, "um", e.target.value)}>
                      {rif.unita.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                    <Input className="col-span-2" type="number" step="0.01" placeholder="Q.tà"
                           value={v.quantita}
                           onChange={(e) => cambiaVoce(i, "quantita", e.target.value)} />
                    <Input className="col-span-2" type="number" step="0.01" placeholder="Prezzo un."
                           value={v.prezzo_unitario}
                           data-testid={`prezzo-${i}`}
                           onChange={(e) => cambiaVoce(i, "prezzo_unitario", e.target.value)} />
                    <button type="button" className="col-span-1 h-10 text-slate-400 hover:text-red-600"
                            title="Togli la voce"
                            onClick={() => setVoci((x) => x.filter((_, k) => k !== i))}>
                      <X className="w-4 h-4 mx-auto" />
                    </button>
                  </div>
                  <textarea value={v.descrizione} rows={2}
                            placeholder="Descrizione della voce, per esteso"
                            data-testid={`descrizione-${i}`}
                            className="w-full border border-slate-200 rounded p-2 text-sm"
                            onChange={(e) => cambiaVoce(i, "descrizione", e.target.value)} />
                  <div className="text-right text-xs text-slate-500">
                    importo {euro((Number(v.quantita) || 0) * (Number(v.prezzo_unitario) || 0))}
                  </div>
                </div>
              ))}
            </div>
            <div className="text-right mt-2 font-display text-xl font-black"
                 data-testid="totale-preventivo">
              TOTALE OFFERTA I.E. {euro(totale)}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>IVA</Label>
              <select value={f.reverse_charge}
                      className="w-full h-10 border border-slate-200 rounded px-3 text-sm"
                      onChange={(e) => setF({ ...f, reverse_charge: e.target.value })}>
                <option value="">Da definire (lascia le caselle in bianco)</option>
                <option value="si">Reverse charge: SÌ</option>
                <option value="no">Reverse charge: NO</option>
                <option value="aliquota">Aliquota IVA</option>
              </select></div>
            {f.reverse_charge === "aliquota" && (
              <div><Label>Aliquota</Label>
                <Input value={f.aliquota_iva} placeholder="22%"
                       onChange={(e) => setF({ ...f, aliquota_iva: e.target.value })} /></div>
            )}
          </div>

          <div className="space-y-3 border-t border-slate-200 pt-3">
            <div className="text-xs uppercase tracking-widest text-slate-400">
              Quello che si legge in fondo al PDF
            </div>
            <div><Label>Chiusura</Label>
              <textarea value={f.chiusura} rows={3}
                        data-testid="preventivo-chiusura"
                        className="w-full border border-slate-200 rounded p-2 text-sm"
                        onChange={(e) => setF({ ...f, chiusura: e.target.value })} /></div>
            <div><Label>Avviso sulla tracciabilità</Label>
              <textarea value={f.avviso} rows={5}
                        data-testid="preventivo-avviso"
                        className="w-full border border-slate-200 rounded p-2 text-sm"
                        onChange={(e) => setF({ ...f, avviso: e.target.value })} /></div>
            <p className="text-xs text-slate-500">
              Sono i testi standard dell&apos;azienda: arrivano già scritti e si
              possono cambiare su questo preventivo senza toccare gli altri.
            </p>
          </div>

          <div><Label>Note interne</Label>
            <textarea value={f.note_interne} rows={2}
                      className="w-full border border-slate-200 rounded p-2 text-sm"
                      placeholder="Non compaiono sul PDF"
                      onChange={(e) => setF({ ...f, note_interne: e.target.value })} /></div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
            <Button type="submit" disabled={busy} data-testid="salva-preventivo"
                    className="bg-orange-500 hover:bg-orange-600 text-white">
              <FileText className="w-4 h-4 mr-2" />
              {busy ? "Salvataggio..." : nuovo ? "Crea il preventivo" : "Salva"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
